import React, { useEffect, useMemo, useState } from "react";
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, ComposedChart, Line, LineChart,
  ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { BarChart3, Info, LineChart as LineIcon } from "lucide-react";
import { Panel, Skeleton } from "./ui";
import { api } from "../lib/api";
import { money, num } from "../lib/format";
import { useTranslation } from "react-i18next";

/**
 * "What does this number mean?" — answered against a real company, with live data.
 *
 * The teaching decision worth writing down: every figure and every chart on this page
 * is fetched from the same endpoints the instrument page uses, not baked in as a
 * fixture. A primer with invented numbers teaches someone to read a chart that does
 * not exist, and the first time they open a real one the shapes disagree. The cost is
 * that the copy cannot say "look at the spike in October" — so it doesn't. Every
 * explanation describes what a reader should look *for*, which is the transferable
 * skill anyway.
 *
 * NVIDIA is here because it is liquid, widely known, pays a token dividend and has a
 * volatile enough chart that every feature being explained is actually visible. It is
 * an example and nothing else — see the note rendered at the top, and the Disclaimer
 * the knowledge base already carries.
 */
const PRIMER_TICKER = "NVDA";

const METRICS = [
  "price", "marketCap", "peTrailing", "peForward", "eps", "dividendYield",
  "beta", "week52", "profitMargin", "roe", "revenue", "avgVolume",
];

const AXIS = { fontSize: 11, stroke: "#94a3b8" };
const GRID = "#f1f5f9";
const BRAND = "#0052FF";

function sma(rows, window) {
  return rows.map((row, i) => {
    if (i < window - 1) return { ...row, [`sma${window}`]: null };
    let total = 0;
    for (let j = i - window + 1; j <= i; j += 1) total += rows[j].close;
    return { ...row, [`sma${window}`]: total / window };
  });
}

function ChartBlock({ id, t, children, note }) {
  return (
    <div className="py-5 border-b border-slate-100 last:border-0" data-testid={`primer-chart-${id}`}>
      <h4 className="font-medium text-slate-800 flex items-center gap-2">
        <LineIcon size={15} className="text-brand-500 shrink-0" />
        {t(`kbStock.chart.${id}.title`)}
      </h4>
      <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{t(`kbStock.chart.${id}.body`)}</p>
      <div className="mt-3 h-52 sm:h-60 -mx-1">{children}</div>
      <p className="text-xs text-slate-500 mt-2 leading-relaxed flex gap-1.5">
        <Info size={13} className="text-slate-400 shrink-0 mt-0.5" />
        {note || t(`kbStock.chart.${id}.note`)}
      </p>
    </div>
  );
}

export default function StockPrimer() {
  const { t } = useTranslation();
  const [quote, setQuote] = useState(null);
  const [fund, setFund] = useState(null);
  const [history, setHistory] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    Promise.all([
      api.quote(PRIMER_TICKER),
      api.fundamentals(PRIMER_TICKER),
      api.history(PRIMER_TICKER, "1y", "1d"),
    ])
      .then(([q, f, h]) => {
        setQuote(q.data);
        setFund(f.data || {});
        setHistory(h.data?.data || []);
      })
      .catch(() => setFailed(true));
  }, []);

  const rows = useMemo(() => {
    const raw = Array.isArray(history) ? history : [];
    const cleaned = raw
      .map((p) => ({
        date: (p.date || "").slice(0, 10),
        close: Number(p.close),
        volume: Number(p.volume ?? 0),
      }))
      .filter((p) => p.date && Number.isFinite(p.close));
    return sma(sma(cleaned, 50), 200);
  }, [history]);

  const ccy = fund?.currency || quote?.currency || "USD";

  // Peak-to-date drawdown: how far below its own running high the price has been.
  const drawdown = useMemo(() => {
    let peak = -Infinity;
    return rows.map((r) => {
      peak = Math.max(peak, r.close);
      return { date: r.date, dd: peak > 0 ? ((r.close - peak) / peak) * 100 : 0 };
    });
  }, [rows]);

  // Rebased to 0% at the start, which is the only honest way to compare two things
  // whose prices are nothing alike. Same rule the analytics benchmark follows.
  const rebased = useMemo(() => {
    if (!rows.length) return [];
    const base = rows[0].close;
    return rows.map((r) => ({ date: r.date, pctChange: ((r.close - base) / base) * 100 }));
  }, [rows]);

  const values = {
    price: quote?.price != null ? money(quote.price, ccy) : "—",
    marketCap: fund?.market_cap ? `${money(fund.market_cap / 1e9, ccy)} bn` : "—",
    peTrailing: fund?.trailing_pe ? num(fund.trailing_pe, 1) : "—",
    peForward: fund?.forward_pe ? num(fund.forward_pe, 1) : "—",
    eps: fund?.eps != null ? money(fund.eps, ccy) : "—",
    // A level, not a change — so no leading "+". pct() signs its output, which reads
    // as a movement and would say a 0.03% dividend yield had gone up by that much.
    dividendYield: fund?.dividend_yield != null
      ? `${num(fund.dividend_yield > 1 ? fund.dividend_yield : fund.dividend_yield * 100, 2)}%`
      : "—",
    beta: fund?.beta != null ? num(fund.beta, 2) : "—",
    week52: fund?.week52_low != null && fund?.week52_high != null
      ? `${money(fund.week52_low, ccy)} – ${money(fund.week52_high, ccy)}` : "—",
    profitMargin: fund?.profit_margins != null ? `${num(fund.profit_margins * 100, 1)}%` : "—",
    roe: fund?.return_on_equity != null ? `${num(fund.return_on_equity * 100, 1)}%` : "—",
    revenue: fund?.revenue ? `${money(fund.revenue / 1e9, ccy)} bn` : "—",
    avgVolume: fund?.avg_volume ? num(fund.avg_volume, 0) : "—",
  };

  if (failed) {
    return (
      <Panel className="card p-5" testid="kb-stock-primer">
        <h2 className="font-heading font-semibold tracking-tight text-lg">{t("kbStock.title")}</h2>
        <p className="text-sm text-slate-500 mt-2">{t("kbStock.unavailable")}</p>
      </Panel>
    );
  }
  if (!rows.length) return <Skeleton className="h-96" />;

  return (
    <Panel className="card p-5 sm:p-6" testid="kb-stock-primer">
      <div className="flex items-center gap-2 mb-1">
        <BarChart3 size={17} className="text-brand-500" />
        <h2 className="font-heading font-semibold tracking-tight text-lg">{t("kbStock.title")}</h2>
      </div>
      <p className="text-sm text-slate-600 leading-relaxed">
        {t("kbStock.intro", { name: fund?.name || PRIMER_TICKER, ticker: PRIMER_TICKER })}
      </p>
      {/* Rule 7: one company shown prominently could read as a suggestion. Say it isn't. */}
      <p className="text-xs text-slate-500 mt-2 leading-relaxed rounded-lg bg-slate-50 p-3">
        {t("kbStock.exampleOnly", { name: fund?.name || PRIMER_TICKER })}
      </p>

      {/* ---------------- the numbers ---------------- */}
      <h3 className="font-heading font-semibold tracking-tight mt-6 mb-1">{t("kbStock.numbersTitle")}</h3>
      <p className="text-sm text-slate-500 leading-relaxed">{t("kbStock.numbersBody")}</p>
      <div className="mt-4 divide-y divide-slate-100">
        {METRICS.map((key) => (
          <div key={key} className="py-3.5" data-testid={`primer-metric-${key}`}>
            <div className="flex items-baseline justify-between gap-3">
              <span className="font-medium text-sm text-slate-800">{t(`kbStock.metric.${key}.label`)}</span>
              <span className="tabular text-sm font-semibold text-slate-900 shrink-0">{values[key]}</span>
            </div>
            <p className="text-sm text-slate-600 mt-1 leading-relaxed">{t(`kbStock.metric.${key}.what`)}</p>
            <p className="text-xs text-slate-500 mt-1 leading-relaxed">{t(`kbStock.metric.${key}.watch`)}</p>
          </div>
        ))}
      </div>

      {/* ---------------- the charts ---------------- */}
      <h3 className="font-heading font-semibold tracking-tight mt-8 mb-1">{t("kbStock.chartsTitle")}</h3>
      <p className="text-sm text-slate-500 leading-relaxed">{t("kbStock.chartsBody")}</p>

      <div className="mt-2">
        <ChartBlock id="line" t={t}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={rows} margin={{ top: 6, right: 6, left: 0, bottom: 0 }}>
              <CartesianGrid stroke={GRID} vertical={false} />
              <XAxis dataKey="date" tick={AXIS} minTickGap={48} tickLine={false} axisLine={false} />
              <YAxis tick={AXIS} width={52} tickLine={false} axisLine={false}
                     domain={["dataMin", "dataMax"]} />
              <Tooltip formatter={(v) => money(v, ccy)} />
              <Line type="monotone" dataKey="close" stroke={BRAND} strokeWidth={1.8} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </ChartBlock>

        <ChartBlock id="range" t={t}
                    note={t("kbStock.chart.range.note", {
                      low: values.week52.split("–")[0]?.trim() || "—",
                      high: values.week52.split("–")[1]?.trim() || "—" })}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={rows} margin={{ top: 6, right: 6, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="primerFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={BRAND} stopOpacity={0.28} />
                  <stop offset="100%" stopColor={BRAND} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke={GRID} vertical={false} />
              <XAxis dataKey="date" tick={AXIS} minTickGap={48} tickLine={false} axisLine={false} />
              <YAxis tick={AXIS} width={52} tickLine={false} axisLine={false} domain={[0, "dataMax"]} />
              <Tooltip formatter={(v) => money(v, ccy)} />
              <Area type="monotone" dataKey="close" stroke={BRAND} strokeWidth={1.6}
                    fill="url(#primerFill)" />
              {fund?.week52_high != null && (
                <ReferenceLine y={fund.week52_high} stroke="#10b981" strokeDasharray="4 4" />
              )}
              {fund?.week52_low != null && (
                <ReferenceLine y={fund.week52_low} stroke="#f43f5e" strokeDasharray="4 4" />
              )}
            </AreaChart>
          </ResponsiveContainer>
        </ChartBlock>

        <ChartBlock id="movingAverage" t={t}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={rows} margin={{ top: 6, right: 6, left: 0, bottom: 0 }}>
              <CartesianGrid stroke={GRID} vertical={false} />
              <XAxis dataKey="date" tick={AXIS} minTickGap={48} tickLine={false} axisLine={false} />
              <YAxis tick={AXIS} width={52} tickLine={false} axisLine={false}
                     domain={["dataMin", "dataMax"]} />
              <Tooltip formatter={(v) => (v == null ? "—" : money(v, ccy))} />
              <Line type="monotone" dataKey="close" stroke="#cbd5e1" strokeWidth={1.4} dot={false} />
              <Line type="monotone" dataKey="sma50" stroke={BRAND} strokeWidth={1.8} dot={false}
                    connectNulls />
              <Line type="monotone" dataKey="sma200" stroke="#f59e0b" strokeWidth={1.8} dot={false}
                    connectNulls />
            </LineChart>
          </ResponsiveContainer>
        </ChartBlock>

        <ChartBlock id="volume" t={t}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={rows} margin={{ top: 6, right: 6, left: 0, bottom: 0 }}>
              <CartesianGrid stroke={GRID} vertical={false} />
              <XAxis dataKey="date" tick={AXIS} minTickGap={48} tickLine={false} axisLine={false} />
              <YAxis tick={AXIS} width={52} tickLine={false} axisLine={false}
                     tickFormatter={(v) => `${Math.round(v / 1e6)}M`} />
              <Tooltip formatter={(v) => num(v, 0)} />
              <Bar dataKey="volume" radius={[2, 2, 0, 0]}>
                {rows.map((r, i) => (
                  <Cell key={r.date}
                        fill={i > 0 && r.close < rows[i - 1].close ? "#fda4af" : "#a5b4fc"} />
                ))}
              </Bar>
            </ComposedChart>
          </ResponsiveContainer>
        </ChartBlock>

        <ChartBlock id="rebased" t={t}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={rebased} margin={{ top: 6, right: 6, left: 0, bottom: 0 }}>
              <CartesianGrid stroke={GRID} vertical={false} />
              <XAxis dataKey="date" tick={AXIS} minTickGap={48} tickLine={false} axisLine={false} />
              <YAxis tick={AXIS} width={52} tickLine={false} axisLine={false}
                     tickFormatter={(v) => `${Math.round(v)}%`} />
              <Tooltip formatter={(v) => `${num(v, 1)}%`} />
              <ReferenceLine y={0} stroke="#94a3b8" />
              <Area type="monotone" dataKey="pctChange" stroke={BRAND} strokeWidth={1.6}
                    fill="url(#primerFill)" />
            </AreaChart>
          </ResponsiveContainer>
        </ChartBlock>

        <ChartBlock id="drawdown" t={t}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={drawdown} margin={{ top: 6, right: 6, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="primerDd" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#f43f5e" stopOpacity={0} />
                  <stop offset="100%" stopColor="#f43f5e" stopOpacity={0.3} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke={GRID} vertical={false} />
              <XAxis dataKey="date" tick={AXIS} minTickGap={48} tickLine={false} axisLine={false} />
              <YAxis tick={AXIS} width={52} tickLine={false} axisLine={false}
                     tickFormatter={(v) => `${Math.round(v)}%`} />
              <Tooltip formatter={(v) => `${num(v, 1)}%`} />
              <ReferenceLine y={0} stroke="#94a3b8" />
              <Area type="monotone" dataKey="dd" stroke="#f43f5e" strokeWidth={1.4}
                    fill="url(#primerDd)" />
            </AreaChart>
          </ResponsiveContainer>
        </ChartBlock>
      </div>
    </Panel>
  );
}
